import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { BsPerson, BsBoxArrowRight, BsPersonCircle } from "react-icons/bs";
import { IoCalendarNumber, IoCalendarNumberOutline } from "react-icons/io5";
import { AiFillHome } from "react-icons/ai";
import { TbReport } from "react-icons/tb";
import Logo from "../../components/Logo";
import axios from "axios";

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [studentName, setStudentName] = useState("");
  const [pendingCount, setPendingCount] = useState(0); // State to hold the pending report count
  const menuRef = useRef(null);
  const navigate = useNavigate();

  const baseURL = process.env.REACT_APP_API_BASE_URL;

  const menuItems = [
    { id: "home", label: "Home", icon: AiFillHome, path: "/" },
    { id: "dailyreport", label: "Daily Report", icon: IoCalendarNumber, path: "/daily-report" },
    // { id: "reports", label: "Reports", icon: TbReport, path: "/reports" },
    { id: "attendance", label: "Attendance", icon: IoCalendarNumberOutline, path: "/attendance" },
  ];

  // Fetch student profile name
  useEffect(() => {
    const fetchStudentName = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const response = await axios.get(`${baseURL}students/profile`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setStudentName(response.data.name);
      } catch (error) {
        console.error("Failed to fetch student profile", error);
      }
    };

    fetchStudentName();
  }, [baseURL]);

  // Fetch pending report count
  useEffect(() => {
    const fetchPendingReports = async () => {
        try {
            const token = localStorage.getItem("authToken");
            const response = await axios.get(`${baseURL}report/pending-count`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setPendingCount(response.data.count || 0); // Set pending report count
        } catch (error) {
            console.error("Failed to fetch pending report count", error);
        }
    };

    fetchPendingReports();
}, [baseURL]);


  // Handle outside click to close the menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem("authToken");
    navigate("/login");
  };

  return (
    <nav className="bg-white border-b border-gray-200 px-4 py-2.5 fixed w-full top-0 left-0 z-50">
      <div className="flex justify-between items-center">
        {/* Logo and Menu */}
        <div className="flex items-center">
          <Link to="/" className="flex items-center mr-10">
            <div className="mx-2">
              <Logo height="10" width="10" />
            </div>
          </Link>
          {/* Desktop Menu */}
          <div className="hidden md:flex space-x-8">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.id}
                  to={item.path}
                  className={({ isActive }) =>
                    `flex items-center px-1 py-3 text-gray-700 hover:border-orange-400 relative group ${
                      isActive ? "border-orange-400 text-orange-400 font-medium" : ""
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <Icon className="w-6 h-6 mr-2" />
                      <span>{item.label}</span>
                      {item.id === "reports" && pendingCount > 0 && (
                        <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                          {pendingCount}
                        </span>
                      )}
                      <span className={`absolute bottom-0 left-0 w-full h-0.5 bg-orange-400 transition-transform ${
                        isActive ? "scale-x-100" : "scale-x-0"
                      }`} />
                    </>
                  )}
                </NavLink>
              );
            })}
          </div>
        </div>

        {/* Right side - Welcome and Profile */}
        <div className="relative flex items-center space-x-6">
          {/* 👋 Welcome Name */}
          <div className="text-sm text-gray-600 hidden sm:block">
            {studentName && <>Welcome, <span className="font-medium text-orange-500">{studentName}</span></>}
          </div>

          {/* Profile Button */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="flex items-center text-gray-700 hover:text-orange-500 focus:outline-none"
          >
            <BsPersonCircle className="w-4 h-6 ml-1" />
          </button>

          {menuOpen && (
            <div ref={menuRef} className="absolute top-1 right-0 mt-8 w-56 bg-white border rounded-lg shadow-lg py-2">
              <Link to="/profile" className="flex items-center w-full px-4 py-2 hover:bg-gray-100">
                <BsPerson className="w-4 h-4 mr-2" /> Profile
              </Link>
              <hr />
              <button
                onClick={handleLogout}
                className="flex items-center w-full px-4 py-2 text-red-600 hover:bg-gray-100"
              >
                <BsBoxArrowRight className="w-4 h-4 mr-2" /> Logout
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Menu */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 flex justify-around py-3 px-4 z-50">
        {menuItems.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.id}
              to={item.path}
              className={({ isActive }) =>
                `flex flex-col items-center px-2 text-xs ${
                  isActive ? "border-orange-400 text-orange-400 font-medium" : "text-gray-600"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon className="w-5 h-5 mb-1" />
                  <span>{item.label}</span>
                  {item.id === "reports" && pendingCount > 0 && (
                    <span className="absolute top-0 right-0 bg-red-500 text-white text-[10px] px-1 rounded-full">
                      {pendingCount}
                    </span>
                  )}
                  {isActive && (
                    <span className="w-1.5 h-1.5 bg-orange-400 rounded-full mt-1" />
                  )}
                </>
              )}
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}
